import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiX } from 'react-icons/hi'
import AvatarProfile from 'components/ui/Avatar/AvatarProfile'
import { autorDe } from './notificationHelpers'

// Cinco segundos alcanzan para leer quién te nombró y de qué hallazgo se trata.
// El que necesita más lo consigue dejando el mouse encima, que es de lo que se
// trata la pausa.
const DURACION = 5000

/**
 * Aviso efímero de una notificación, mientras la app está abierta.
 *
 * Se anuncia con role="status" y no con role="alert": el segundo interrumpe al
 * lector de pantalla a mitad de frase y no retoma lo que estaba diciendo. Y
 * nunca se lleva el foco, porque mover el foco por algo que el usuario no pidió
 * es una falla de accesibilidad, no una comodidad.
 */
const NotificationToast = ({ notification, onClose, onOpen }) => {
	const { t } = useTranslation()
	const [pausado, setPausado] = useState(false)
	const [saliendo, setSaliendo] = useState(false)

	// Cuánto queda del tiempo, y desde cuándo corre el tramo actual. Sin esto, al
	// sacar el mouse el aviso arrancaría de cero y un roce lo volvería eterno.
	const restante = useRef(DURACION)
	const desde = useRef(Date.now())
	const timer = useRef(null)
	const cerrado = useRef(false)

	useEffect(() => {
		const cerrar = () => {
			if (cerrado.current) return
			cerrado.current = true
			setSaliendo(true)
			// Se saca de la lista después de la animación de salida, no antes: si
			// se desmonta de una, el aviso desaparece de golpe.
			setTimeout(() => onClose(notification.id), 180)
		}

		if (pausado) {
			clearTimeout(timer.current)
			restante.current = Math.max(0, restante.current - (Date.now() - desde.current))
			return undefined
		}

		desde.current = Date.now()
		timer.current = setTimeout(cerrar, restante.current)

		return () => clearTimeout(timer.current)
	}, [pausado, notification.id, onClose])

	const cerrarAMano = (e) => {
		e.stopPropagation()
		clearTimeout(timer.current)
		cerrado.current = true
		setSaliendo(true)
		setTimeout(() => onClose(notification.id), 180)
	}

	const autor = autorDe(notification)
	const tieneAutor = Boolean(autor.name)

	return (
		<div
			role="status"
			onClick={() => onOpen(notification)}
			onMouseEnter={() => setPausado(true)}
			onMouseLeave={() => setPausado(false)}
			onFocus={() => setPausado(true)}
			onBlur={() => setPausado(false)}
			className={`relative flex gap-3 w-full p-3 pr-9 rounded-lg cursor-pointer overflow-hidden
				bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
				shadow-lg transition-all duration-200
				${saliendo ? 'opacity-0 translate-x-4' : 'opacity-100 translate-x-0'}`}
		>
			{tieneAutor && <AvatarProfile user={autor} size={34} />}

			<div className="flex-1 min-w-0">
				<p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
					{tieneAutor ? autor.name : notification.title}
				</p>
				{tieneAutor && (
					<p className="text-xs text-gray-500 dark:text-gray-400">
						{t('mentioned_you_in', 'te mencionó en')} {t('ticket_alias')} #{notification.data?.ticketId}
					</p>
				)}
				{notification.message && (
					<p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
						{notification.message}
					</p>
				)}
			</div>

			{/* Cerrar no es leer: saca el aviso de la pantalla sin tocar el punto
			    de la campanita, que es lo que la persona espera de una X. */}
			<button
				type="button"
				onClick={cerrarAMano}
				aria-label={t('close', 'Cerrar')}
				className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-md
					text-gray-400 hover:text-gray-700 dark:hover:text-gray-200
					hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
			>
				<HiX />
			</button>

			<span
				className="absolute left-0 bottom-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 origin-left"
				style={{
					width: '100%',
					animation: `storehub-toast-drain ${DURACION}ms linear forwards`,
					animationPlayState: pausado ? 'paused' : 'running',
				}}
			/>
		</div>
	)
}

export default NotificationToast
